import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { ZardSegmentedComponent, SegmentedOption } from '../segmented/segmented.component';

@Component({
    selector: 'app-select-button',
    imports: [CommonModule, ZardSegmentedComponent, FormsModule],
    standalone: true,
    templateUrl: './select-button.component.html',
    styleUrl: './select-button.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectButtonComponent),
            multi: true
        }
    ]
})
export class SelectButtonComponent implements ControlValueAccessor, AfterViewInit {
    @Input() options: SegmentedOption[] = [];
    @Input() multiple: boolean = false;
    @Input() disabled: boolean = false;
    @Input() invalid: boolean = false;
    @Input() size: 'small' | 'large' | undefined = undefined;
    @Input() variant: 'default' | 'rounded' = 'default';
    @Input() severity: 'default' | 'ai-light-gradient' = 'default';
    @Input() fluid: boolean = false;
    @Input() name: string = '';
    @Input() required: boolean = false;
    @Output() selectionChange = new EventEmitter<any>();

    @ViewChild(ZardSegmentedComponent) segmentedComponent?: ZardSegmentedComponent;

    private _value: any = null;
    private onChange = (value: any) => { };
    private onTouched = () => { };

    get value(): any {
        return this._value;
    }

    set value(val: any) {
        this._value = val;
        this.onChange(val);
        this.selectionChange.emit(val);
    }

    get segmentedOptions(): SegmentedOption[] {
        return this.options.map(option => {
            let label = option.label;
            if (!label) {
                label = String(option.value);
            }

            return {
                value: String(option.value),
                label: label,
                disabled: option.disabled || false,
                icon: option.icon,
                iconSource: option.iconSource,
                iconClass: option.iconClass
            };
        });
    }

    get zSize(): 'sm' | 'default' | 'lg' {
        if (this.size === 'small') return 'sm';
        if (this.size === 'large') return 'lg';
        return 'default';
    }

    get zDefaultValue(): string {
        return this._value != null ? String(this._value) : '';
    }

    ngAfterViewInit(): void {
        if (this._value != null && this.segmentedComponent) {
            this.segmentedComponent.writeValue(String(this._value));
        }
    }

    writeValue(value: any): void {
        this._value = value;
        if (this.segmentedComponent) {
            this.segmentedComponent.writeValue(value != null ? String(value) : '');
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onSelectionChange(value: string): void {
        const originalOption = this.options.find(opt => String(opt.value) === value);
        const convertedValue = originalOption ? originalOption.value : value;

        this.value = convertedValue;
        this.onTouched();
    }
}
